import styled from '@emotion/styled';
import { Button, Form, Input, notification } from 'antd';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FiLock, FiMail, FiShield } from 'react-icons/fi';
import loginBG from '../../assets/images/login_back.jpg';
import logo from '../../assets/images/logo_trans.png';
import { createUser } from '../../redux/userStore/actions';
import { UserStateType } from '../../redux/userStore/reducer';
import { ROUTES } from '../../routes';
import { ORANGE, PRIMARY, YELLOW } from '../../shared/colors';
import { checkProfile } from '../shared/ProtectedRoutes';
import { signIn, verify } from './network';

type LayoutType = Parameters<typeof Form>[0]['layout'];

const SignInContainer = styled.div`
  min-height: 100vh;
  background-image:
    linear-gradient(120deg, rgba(3, 8, 13, 0.96), rgba(4, 16, 24, 0.84) 48%, rgba(27, 12, 8, 0.78)),
    url(${loginBG});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  font-family: Poppins-R;
  color: #fff3dc;
  overflow: hidden;

  .login-shell {
    position: relative;
    display: grid;
    grid-template-columns: minmax(0, 1.05fr) minmax(390px, 0.72fr);
    align-items: center;
    gap: clamp(32px, 6vw, 86px);
    min-height: 100vh;
    padding: clamp(28px, 6vw, 72px);
    isolation: isolate;
  }

  .login-shell::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -2;
    background:
      radial-gradient(circle at 16% 18%, rgba(255, 79, 31, 0.16), transparent 28%),
      radial-gradient(circle at 70% 24%, rgba(255, 176, 0, 0.12), transparent 24%),
      linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px),
      linear-gradient(180deg, rgba(255, 255, 255, 0.026) 1px, transparent 1px);
    background-size: auto, auto, 72px 72px, 72px 72px;
  }

  .login-watermark {
    position: absolute;
    left: -70px;
    bottom: -110px;
    z-index: -1;
    width: min(52vw, 680px);
    opacity: 0.08;
    filter: saturate(1.25);
    pointer-events: none;
  }

  .brand-panel {
    max-width: 760px;
  }

  .brand-mark {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 38px;
  }

  .brand-logo {
    width: 66px;
    height: 66px;
    border-radius: 50%;
    object-fit: contain;
    filter: drop-shadow(0 10px 24px rgba(0, 0, 0, 0.36));
  }

  .brand-name {
    font-family: Poppins-B;
    font-size: clamp(25px, 2.5vw, 38px);
    line-height: 1;
  }

  .brand-name .orange {
    color: ${ORANGE};
  }

  .brand-name .e {
    color: #fff3dc;
  }

  .brand-name .gold {
    color: ${YELLOW};
  }

  .brand-title {
    max-width: 760px;
    margin: 0;
    color: #f8fafc;
    font-family: Poppins-B;
    font-size: clamp(42px, 5vw, 76px);
    line-height: 0.98;
    letter-spacing: 0;
  }

  .brand-copy {
    max-width: 610px;
    margin: 22px 0 0;
    color: rgba(255, 243, 220, 0.78);
    font-size: 16px;
    line-height: 1.75;
  }

  .login-card {
    width: min(100%, 430px);
    justify-self: center;
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 18px;
    padding: 28px;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(248, 250, 252, 0.92));
    color: #0f1d29;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.38);
    backdrop-filter: blur(18px);
  }

  .login-card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 24px;
  }

  .login-card-brand {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .login-card-logo {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    object-fit: contain;
  }

  .login-card-title {
    margin: 0;
    font-family: Poppins-B;
    font-size: 21px;
    line-height: 1;
  }

  .login-card-title .orange {
    color: ${ORANGE};
  }

  .login-card-title .e {
    color: #0f1d29;
  }

  .login-card-title .gold {
    color: ${YELLOW};
  }

  .login-intro {
    margin-bottom: 22px;
  }

  .login-intro span {
    color: #637286;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .login-intro h1 {
    margin: 4px 0 0;
    color: #0f1d29;
    font-family: Poppins-B;
    font-size: 24px;
  }

  .premium-form .ant-form-item {
    margin-bottom: 16px;
  }

  .premium-form .ant-form-item-label > label {
    color: #334155;
    font-family: Poppins-B;
    font-size: 12px;
  }

  .premium-form .ant-input,
  .premium-form .ant-input-affix-wrapper {
    height: 44px;
    border-color: #cbd5e1 !important;
    border-radius: 8px;
    background: #f8fafc !important;
    color: #0f1d29 !important;
    box-shadow: none;
  }

  .premium-form .ant-input-affix-wrapper input {
    background: transparent !important;
    color: #0f1d29 !important;
  }

  .premium-form .ant-input-prefix {
    margin-right: 9px;
    color: #64748b;
  }

  .premium-form .ant-input-affix-wrapper-focused,
  .premium-form .ant-input:focus {
    border-color: ${ORANGE} !important;
    box-shadow: 0 0 0 3px rgba(255, 79, 31, 0.12) !important;
  }

  .ant-form-item-explain.ant-form-item-explain-error {
    font-size: 11px;
  }

  .forgot-row {
    display: flex;
    justify-content: flex-end;
    margin: -6px 0 14px;
  }

  .forgot-row button {
    padding: 0;
    border: 0;
    background: transparent;
    color: ${ORANGE};
    font-family: Poppins-B;
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
  }

  .submit-button {
    width: 100%;
    height: 46px;
    border: 0;
    border-radius: 999px;
    background: linear-gradient(135deg, ${ORANGE}, #ff7a18);
    color: white;
    font-family: Poppins-B;
    box-shadow: 0 14px 28px rgba(255, 79, 31, 0.26);
  }

  .submit-button:hover,
  .submit-button:focus {
    background: linear-gradient(135deg, #ff6a35, ${YELLOW});
    color: #07141c;
  }

  .signin-footer {
    display: flex;
    justify-content: center;
    gap: 6px;
    margin-top: -4px;
    color: #64748b;
    font-size: 12px;
    text-align: center;
  }

  .signin-footer button {
    padding: 0;
    border: 0;
    background: transparent;
    color: ${PRIMARY};
    font-family: Poppins-B;
    text-decoration: underline;
    cursor: pointer;
  }

  .security-note {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
    color: #64748b;
    font-size: 11px;
  }

  @media (max-width: 980px) {
    overflow: auto;

    .login-shell {
      grid-template-columns: 1fr;
      gap: 30px;
      padding: 28px 18px;
    }

    .brand-panel {
      text-align: center;
      justify-self: center;
    }

    .brand-mark {
      justify-content: center;
      margin-bottom: 24px;
    }

    .brand-copy {
      margin-left: auto;
      margin-right: auto;
    }

  }

  @media (max-width: 767px) {
    .brand-title {
      font-size: 38px;
    }

    .login-card {
      padding: 22px 18px;
    }

    .login-card-header {
      align-items: flex-start;
      flex-direction: column;
    }
  }
`;

export const LoginPage = () => {
  const [form] = Form.useForm();
  const [formLayout] = useState<LayoutType>('vertical');
  const [isLoading, setIsLoading] = useState(false);
  const [isOtp, setisOTP] = useState(false);
  const [runningU, setRunningU] = useState<UserStateType | undefined>();
  const router = useHistory();
  const dispatch = useDispatch();

  const redirectToPage = () => {
    if (runningU) {
      dispatch(createUser(runningU));
      if (checkProfile(runningU.profils as any, 'admin')) {
        router.push(ROUTES.DASHBOARD.PAGE);
      } else if (checkProfile(runningU.profils as any, 'resp_commercial')) {
        router.push(ROUTES.DASHBOARD.PAGE);
      } else if (checkProfile(runningU.profils as any, 'commercial')) {
        router.push(ROUTES.DASHBOARD.PAGE);
      } else if (checkProfile(runningU.profils as any, 'responsable')) {
        router.push(ROUTES.DASHBOARD.PAGE);
      } else if (checkProfile(runningU.profils as any, 'chef_agence')) {
        router.push(ROUTES.DASHBOARD.PAGE);
      } else if (checkProfile(runningU.profils as any, 'gestionnaire')) {
        router.push(ROUTES.DASHBOARD.PAGE);
      } else if (checkProfile(runningU.profils as any, 'sadmin')) {
        router.push(ROUTES.DASHBOARD.PAGE);
      }
    }
  };

  return (
    <SignInContainer>
      <div className='login-shell'>
        <img className='login-watermark' src={logo} alt='' />

        <section className='brand-panel'>
          <div className='brand-mark'>
            <img className='brand-logo' src={logo} alt='MoranjETrade' />
            <div className='brand-name'>
              <span className='orange'>Moranj</span>
              <span className='e'>E</span>
              <span className='gold'>Trade</span>
            </div>
          </div>

          <h1 className='brand-title'>
            Le terminal obligataire pour l'Afrique financière.
          </h1>

          <p className='brand-copy'>
            Connectez-vous à votre espace de trading pour suivre les titres,
            consulter les carnets d'ordres et gérer les portefeuilles régionaux
            depuis une interface conçue pour les desks institutionnels.
          </p>

        </section>

        <section className='login-card'>
          <div className='login-card-header'>
            <div className='login-card-brand'>
              <img className='login-card-logo' src={logo} alt='MoranjETrade' />
              <p className='login-card-title'>
                <span className='orange'>Moranj</span>
                <span className='e'>E</span>
                <span className='gold'>Trade</span>
              </p>
            </div>
          </div>

          <div className='login-intro'>
            <span>{isOtp ? 'Validation OTP' : 'Connexion'}</span>
            <h1>{isOtp ? 'Valider la session' : 'Accéder au terminal'}</h1>
          </div>

          <Form
            className='premium-form'
            layout={formLayout}
            form={form}
            labelCol={{ span: 20 }}
            wrapperCol={{ span: 25 }}
            onFinish={async (data) => {
              if (runningU) {
                const { code }: { code: string } = data;
                // if (code === '1234') {
                //   redirectToPage();
                // } else {
                //   notification.error({
                //     message: 'Erreur',
                //     description: 'Code incorrect',
                //   });
                // }
                // return;
                await verify(runningU.email, code)
                  .then(async ({ statusCode, data }) => {
                    if (data.ok) {
                      redirectToPage();
                      // Tu peux rediriger vers une autre page ici
                    } else {
                      setRunningU(undefined);
                      notification.error({
                        message: 'Erreur',
                        description: data.error,
                      });
                    }
                  })
                  .catch((err) => {
                    setRunningU(undefined);
                    notification.error({
                      message: 'Erreur',
                      description: err.message || err,
                    });
                    setIsLoading(false);
                  });
              } else {
                setIsLoading(true);
                const { email, password } = data;
                await signIn(email, password)
                  .then(async ({ statusCode, data }) => {
                    setIsLoading(false);
                    if (statusCode.toString()[0] === '2') {
                      console.log(data);
                      setRunningU({
                        authentifie: true,
                        id: data.id,
                        agence: data.agence,
                        url: data.url,
                        nom: data.nom,
                        prenom: data.prenom,
                        email: data.email,
                        profils: data.profils,
                        manager: data.manager,
                        partenaire: data.partenaire,
                        token: '',
                        nextAuthDate:
                          new Date().getTime() + 12 * 60 * 60 * 1000,
                        achat: 0,
                        vente: 0,
                      });
                      setisOTP(true);
                    } else {
                      notification.error({
                        message: 'Erreur',
                        description: data.message,
                      });
                      setIsLoading(false);
                    }
                  })
                  .catch((err) => {
                    notification.error({
                      message: 'Erreur',
                      description: err.message || err,
                    });
                    setIsLoading(false);
                  });
              }
            }}
          >
            {isOtp ? (
              <>
                <Form.Item
                  label='OTP'
                  name='code'
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: 'Le champ otp est requis',
                    },
                  ]}
                >
                  <Input
                    prefix={<FiShield />}
                    placeholder='Entrer le code otp reçu par SMS'
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    className='submit-button'
                    type='primary'
                    htmlType='submit'
                    size='large'
                    loading={isLoading}
                  >
                    Vérifier
                  </Button>
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  label='Email'
                  name='email'
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      type: 'email',
                      message: 'Le champ email est requis',
                    },
                  ]}
                >
                  <Input
                    prefix={<FiMail />}
                    placeholder='adresse mail'
                    type='email'
                  />
                </Form.Item>
                <Form.Item
                  label='Mot de passe'
                  name='password'
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: 'Le mot de passe est requis',
                    },
                  ]}
                >
                  <Input.Password
                    prefix={<FiLock />}
                    placeholder='mot de passe'
                  />
                </Form.Item>

                <div className='forgot-row'>
                  <button type='button'>
                    Mot de passe oublié ?
                  </button>
                </div>

                <Form.Item>
                  <Button
                    className='submit-button'
                    type='primary'
                    htmlType='submit'
                    size='large'
                    loading={isLoading}
                  >
                    Se connecter au terminal
                  </Button>
                  <div className='signin-footer'>
                    <span>Vous n'avez pas de compte ?</span>
                    <button
                      type='button'
                      onClick={() => router.push(ROUTES.SIGNUP)}
                    >
                      Créer un compte
                    </button>
                  </div>
                </Form.Item>
              </>
            )}
          </Form>

          <div className='security-note'>
            <FiShield />
            Session chiffrée · Accès réservé aux utilisateurs autorisés
          </div>
        </section>
      </div>
    </SignInContainer>
  );
};
